import { useEffect, useState } from "react"
import { motion, useReducedMotion } from "framer-motion"

export type ServiceKey = "mvp" | "ai" | "team"

const SERVICE_LINKS: Record<ServiceKey, string> = {
  mvp: "/services/mvp-development",
  ai: "/services/ai-workflow-automation",
  team: "/services/dedicated-developers",
}

interface HeroVelocityEngineProps {
  activeService: ServiceKey | null
  onServiceHover: (service: ServiceKey | null) => void
}

function FlowDot({
  pathData,
  delay = 0,
  duration = 1.5,
  isActive = true,
}: {
  pathData: string
  delay?: number
  duration?: number
  isActive?: boolean
  id: string
}) {
  const prefersReducedMotion = useReducedMotion()
  if (prefersReducedMotion || !isActive) return null

  return (
    <motion.circle
      r="5"
      fill="#ed2132"
      initial={{ opacity: 0 }}
      animate={{
        opacity: [0, 1, 1, 0],
        offsetDistance: ["0%", "100%"],
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        repeatDelay: 0.5,
        times: [0, 0.1, 0.9, 1],
        ease: "linear",
      }}
      style={{
        offsetPath: `path("${pathData}")`,
        offsetRotate: "0deg",
      }}
    />
  )
}

export function HeroVelocityEngine({ activeService, onServiceHover }: HeroVelocityEngineProps) {
  const prefersReducedMotion = useReducedMotion()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleCardClick = (service: ServiceKey) => {
    window.location.href = SERVICE_LINKS[service]
  }

  const getCardOpacity = (service: ServiceKey) => {
    if (activeService === null) return 1
    return activeService === service ? 1 : 0.15
  }

  const getCardStroke = (service: ServiceKey) =>
    activeService === service ? "#ed2132" : "#E2E8F0"

  const getCardStrokeWidth = (service: ServiceKey) =>
    activeService === service ? 2.5 : 1.5

  const flowPaths = {
    mvp: "M 310 380 Q 450 440 680 480",
    ai: "M 800 380 L 800 450",
    team: "M 1290 380 Q 1150 440 920 480",
  }

  const progressBarVariants = {
    animate: {
      scaleX: [0, 1, 1, 0],
      transition: { duration: 6, repeat: Infinity, ease: "easeOut", times: [0, 0.85, 0.95, 1] },
    },
  }

  const wireframeVariants = {
    animate: {
      opacity: [0, 0, 1, 1, 0, 0],
      transition: { duration: 8, repeat: Infinity, times: [0, 0.45, 0.5, 0.95, 1, 1] },
    },
  }

  const polishedVariants = {
    animate: {
      opacity: [1, 1, 0, 0, 1, 1],
      transition: { duration: 8, repeat: Infinity, times: [0, 0.45, 0.5, 0.95, 1, 1] },
    },
  }

  const taskPillVariants = (index: number) => ({
    animate: {
      opacity: [0, 0, 1, 1, 1, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        times: [0, index * 0.15, index * 0.15 + 0.1, 0.7, 0.85, 1],
        ease: "easeInOut",
      },
    },
  })

  const robotDotVariants = (index: number) => ({
    animate: {
      opacity: [0.3, 1, 0.3],
      scale: [0.8, 1.2, 0.8],
      transition: { duration: 1.5, delay: index * 0.3, repeat: Infinity, ease: "easeInOut" },
    },
  })

  const checkmarkVariants = {
    animate: {
      scale: [1, 1.15, 1],
      opacity: [0.8, 1, 0.8],
      transition: { duration: 2, repeat: Infinity, ease: "easeInOut" },
    },
  }

  const fanoutLineVariants = (index: number) => ({
    animate: {
      strokeDashoffset: [0, -24],
      transition: { duration: 1, delay: index * 0.3, repeat: Infinity, ease: "linear" },
    },
  })

  const commitBarVariants = (index: number) => ({
    animate: {
      scaleY: [0, 1, 1, 0],
      transition: { duration: 5.4, delay: index * 0.08, repeat: Infinity, times: [0, 0.26, 0.74, 1] },
    },
  })

  const prCardVariants = (index: number) => ({
    animate: {
      opacity: [0, 1, 1, 0],
      transition: {
        duration: 4,
        delay: index * 1.5,
        repeat: Infinity,
        repeatDelay: 2,
        times: [0, 0.15, 0.85, 1],
        ease: "easeInOut",
      },
    },
  })

  const statusDotVariants = {
    animate: {
      opacity: [1, 0.3, 1],
      scale: [1, 1.3, 1],
      transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
    },
  }

  if (!mounted) {
    return <div className="w-full aspect-[16/6]" />
  }

  return (
    <div className="w-full">
      <svg
        viewBox="0 0 1600 560"
        className="w-full h-auto"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Interactive service visualization showing MVP Development, AI Automation, and Dedicated Developers"
      >
        <defs>
          <radialGradient id="centerGlow" cx="50%" cy="90%" r="50%">
            <stop offset="0%" stopColor="#ed2132" stopOpacity="0.2" />
            <stop offset="55%" stopColor="#ed2132" stopOpacity="0.05" />
            <stop offset="100%" stopColor="#ed2132" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="gridFade" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="70%" stopColor="#ffffff" stopOpacity="0" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
          </radialGradient>
          <pattern id="dotGrid" x="0" y="0" width="32" height="32" patternUnits="userSpaceOnUse" patternTransform="skewX(-30) scale(1, 0.866)">
            <circle cx="16" cy="16" r="1.1" fill="#E2E8F0" />
          </pattern>
          <clipPath id="browserClipMvp">
            <rect x="100" y="108" width="360" height="200" rx="8" />
          </clipPath>
        </defs>

        <rect width="1600" height="560" fill="#FFFFFF" />
        <rect width="1600" height="560" fill="url(#dotGrid)" opacity="0.9" />
        <rect width="1600" height="560" fill="url(#gridFade)" />
        <ellipse cx="800" cy="520" rx="400" ry="180" fill="url(#centerGlow)" />

        <g fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="5 6" strokeLinecap="round">
          <motion.path d={flowPaths.mvp} animate={{ opacity: activeService === null || activeService === "mvp" ? 1 : 0.15 }} transition={{ duration: 0.3 }} />
          <motion.path d={flowPaths.ai} animate={{ opacity: activeService === null || activeService === "ai" ? 1 : 0.15 }} transition={{ duration: 0.3 }} />
          <motion.path d={flowPaths.team} animate={{ opacity: activeService === null || activeService === "team" ? 1 : 0.15 }} transition={{ duration: 0.3 }} />
        </g>

        {!prefersReducedMotion && (
          <>
            <FlowDot id="mvp-dot-1" pathData={flowPaths.mvp} delay={0} isActive={activeService === null || activeService === "mvp"} />
            <FlowDot id="mvp-dot-2" pathData={flowPaths.mvp} delay={0.5} isActive={activeService === null || activeService === "mvp"} />
            <FlowDot id="ai-dot-1" pathData={flowPaths.ai} delay={0.2} isActive={activeService === null || activeService === "ai"} />
            <FlowDot id="ai-dot-2" pathData={flowPaths.ai} delay={0.7} isActive={activeService === null || activeService === "ai"} />
            <FlowDot id="team-dot-1" pathData={flowPaths.team} delay={0.4} isActive={activeService === null || activeService === "team"} />
            <FlowDot id="team-dot-2" pathData={flowPaths.team} delay={0.9} isActive={activeService === null || activeService === "team"} />
          </>
        )}

        {/* CARD 1: MVP DEVELOPMENT */}
        <motion.g
          animate={{ opacity: getCardOpacity("mvp") }}
          transition={{ duration: 0.3 }}
          onMouseEnter={() => onServiceHover("mvp")}
          onMouseLeave={() => onServiceHover(null)}
          onClick={() => handleCardClick("mvp")}
          style={{ cursor: "pointer" }}
        >
          <motion.rect
            x="60" y="20" width="440" height="360" rx="14"
            fill="#FFFFFF"
            animate={{ stroke: getCardStroke("mvp"), strokeWidth: getCardStrokeWidth("mvp") }}
            transition={{ duration: 0.3 }}
          />
          <text x="88" y="52" fontSize="11" fontWeight="700" letterSpacing="1.4" fill="#475569">MVP DEVELOPMENT</text>

          <g transform="translate(88, 68)">
            <text x="0" y="0" fontSize="10" letterSpacing="0.6" fill="#475569">30 DAYS</text>
            <text x="384" y="0" fontSize="10" letterSpacing="0.6" fill="#475569" textAnchor="end">DAY 24</text>
          </g>

          <g transform="translate(88, 78)">
            {Array.from({ length: 30 }, (_, i) => (
              <rect key={`bg-${i}`} x={i * 13} y="0" width="10" height="6" rx="2" fill="#E2E8F0" />
            ))}
          </g>

          <g transform="translate(88, 78)">
            {Array.from({ length: 30 }, (_, i) => (
              <motion.rect
                key={`fill-${i}`}
                x={i * 13} y="0" width="10" height="6" rx="2" fill="#ed2132"
                initial={{ opacity: 0 }}
                animate={!prefersReducedMotion ? { opacity: [0, 0, 1, 1, 0] } : { opacity: 1 }}
                transition={{ duration: 6, repeat: Infinity, times: [0, i / 36, (i + 1) / 36 + 0.02, 0.95, 1], ease: "easeOut" }}
              />
            ))}
          </g>

          <g>
            <rect x="100" y="108" width="360" height="200" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1.5" />
            <rect x="100" y="108" width="360" height="26" rx="8" fill="#FFFFFF" />
            <rect x="100" y="128" width="360" height="6" fill="#FFFFFF" />
            <line x1="100" y1="134" x2="460" y2="134" stroke="#E2E8F0" strokeWidth="1.5" />
            <circle cx="116" cy="121" r="4" fill="#E2E8F0" />
            <circle cx="130" cy="121" r="4" fill="#E2E8F0" />
            <circle cx="144" cy="121" r="4" fill="#E2E8F0" />
            <rect x="166" y="113" width="200" height="16" rx="4" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
          </g>

          <motion.g clipPath="url(#browserClipMvp)" variants={!prefersReducedMotion ? wireframeVariants : undefined} animate={!prefersReducedMotion ? "animate" : undefined} initial={{ opacity: 0 }}>
            <rect x="118" y="148" width="20" height="20" rx="3" fill="#E2E8F0" />
            <rect x="148" y="152" width="100" height="6" rx="3" fill="#E2E8F0" />
            <rect x="148" y="164" width="64" height="5" rx="2" fill="#E2E8F0" />
            <rect x="118" y="182" width="200" height="14" rx="3" fill="#E2E8F0" />
            <rect x="118" y="204" width="160" height="10" rx="3" fill="#E2E8F0" />
            <rect x="118" y="222" width="200" height="10" rx="3" fill="#E2E8F0" />
            <rect x="118" y="240" width="120" height="10" rx="3" fill="#E2E8F0" />
            <rect x="118" y="260" width="200" height="40" rx="4" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
            <rect x="340" y="182" width="100" height="28" rx="6" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
            <rect x="340" y="218" width="100" height="28" rx="6" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
            <rect x="340" y="254" width="48" height="40" rx="4" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
            <rect x="392" y="254" width="48" height="40" rx="4" fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4" />
          </motion.g>

          <motion.g clipPath="url(#browserClipMvp)" variants={!prefersReducedMotion ? polishedVariants : undefined} animate={!prefersReducedMotion ? "animate" : undefined} initial={{ opacity: 1 }}>
            <circle cx="128" cy="158" r="10" fill="#FEE2E2" />
            <text x="128" y="162" fontSize="9" fontWeight="700" fill="#ed2132" textAnchor="middle">A</text>
            <rect x="148" y="153" width="72" height="5" rx="2" fill="#0F172A" />
            <rect x="148" y="163" width="50" height="4" rx="2" fill="#E2E8F0" />
            <rect x="118" y="180" width="200" height="110" rx="6" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
            <text x="128" y="196" fontSize="8" fontWeight="600" fill="#475569" letterSpacing="0.5">REVENUE</text>
            <line x1="128" y1="206" x2="308" y2="206" stroke="#F8FAFC" strokeWidth="1" />
            <line x1="128" y1="230" x2="308" y2="230" stroke="#F8FAFC" strokeWidth="1" />
            <line x1="128" y1="254" x2="308" y2="254" stroke="#F8FAFC" strokeWidth="1" />
            <polyline points="130,276 150,266 170,270 190,254 210,244 230,236 250,218 270,224 308,206" fill="none" stroke="#ed2132" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
            <circle cx="308" cy="206" r="3" fill="#ed2132" />
            <rect x="340" y="180" width="100" height="28" rx="6" fill="#ed2132" />
            <text x="390" y="198" fontSize="9" fontWeight="600" fill="#FFFFFF" textAnchor="middle">Deploy</text>
            <rect x="340" y="216" width="100" height="28" rx="6" fill="#FFFFFF" stroke="#ed2132" strokeWidth="1.5" />
            <text x="390" y="234" fontSize="9" fontWeight="600" fill="#ed2132" textAnchor="middle">Preview</text>
            <rect x="340" y="252" width="48" height="36" rx="4" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
            <text x="346" y="266" fontSize="7" fill="#475569">USERS</text>
            <text x="346" y="282" fontSize="10" fontWeight="700" fill="#0F172A">2,431</text>
            <rect x="392" y="252" width="48" height="36" rx="4" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
            <text x="398" y="266" fontSize="7" fill="#475569">MRR</text>
            <text x="398" y="282" fontSize="10" fontWeight="700" fill="#0F172A">$48k</text>
          </motion.g>

          <g transform="translate(88, 320)">
            <rect x="0" y="0" width="380" height="56" rx="28" fill="#FEE2E2" />
            <circle cx="28" cy="28" r="10" fill="#ed2132" />
            <text x="50" y="38" fontSize="26" fontWeight="600" fill="#ed2132">Shipped in 30 days</text>
          </g>
        </motion.g>

        {/* CARD 2: AI AUTOMATION */}
        <motion.g
          animate={{ opacity: getCardOpacity("ai") }}
          transition={{ duration: 0.3 }}
          onMouseEnter={() => onServiceHover("ai")}
          onMouseLeave={() => onServiceHover(null)}
          onClick={() => handleCardClick("ai")}
          style={{ cursor: "pointer" }}
        >
          <motion.rect
            x="580" y="20" width="440" height="360" rx="14"
            fill="#FFFFFF"
            animate={{ stroke: getCardStroke("ai"), strokeWidth: getCardStrokeWidth("ai") }}
            transition={{ duration: 0.3 }}
          />
          <text x="608" y="52" fontSize="11" fontWeight="700" letterSpacing="1.4" fill="#475569">AI AUTOMATION</text>

          <g fill="none" stroke="#E2E8F0" strokeWidth="1.5" strokeDasharray="4 4">
            <path d="M 738 95 Q 770 120 800 150" />
            <path d="M 738 135 Q 770 150 800 165" />
            <path d="M 738 175 Q 770 180 800 180" />
          </g>

          <g transform="translate(608, 75)">
            <motion.g variants={!prefersReducedMotion ? taskPillVariants(0) : undefined} animate={!prefersReducedMotion ? "animate" : undefined} initial={{ opacity: 1 }}>
              <rect x="0" y="0" width="130" height="30" rx="15" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
              <circle cx="16" cy="15" r="5" fill="#22C55E" />
              <text x="28" y="19" fontSize="11" fill="#0F172A">Lead intake</text>
            </motion.g>
            <motion.g transform="translate(0, 40)" variants={!prefersReducedMotion ? taskPillVariants(1) : undefined} animate={!prefersReducedMotion ? "animate" : undefined} initial={{ opacity: 1 }}>
              <rect x="0" y="0" width="130" height="30" rx="15" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
              <circle cx="16" cy="15" r="5" fill="#F59E0B" />
              <text x="28" y="19" fontSize="11" fill="#0F172A">Invoice</text>
            </motion.g>
            <motion.g transform="translate(0, 80)" variants={!prefersReducedMotion ? taskPillVariants(2) : undefined} animate={!prefersReducedMotion ? "animate" : undefined} initial={{ opacity: 1 }}>
              <rect x="0" y="0" width="130" height="30" rx="15" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
              <circle cx="16" cy="15" r="5" fill="#3B82F6" />
              <text x="28" y="19" fontSize="11" fill="#0F172A">Email triage</text>
            </motion.g>
          </g>

          <g transform="translate(800, 165)">
            <circle r="42" fill="none" stroke="#FECACA" strokeWidth="1.5" strokeDasharray="4 3" />
            <circle r="36" fill="#FEE2E2" />
            {!prefersReducedMotion && (
              <>
                <motion.circle cx="-30" cy="-30" r="4" fill="#ed2132" variants={robotDotVariants(0)} animate="animate" />
                <motion.circle cx="30" cy="-30" r="4" fill="#ed2132" variants={robotDotVariants(1)} animate="animate" />
                <motion.circle cx="0" cy="42" r="4" fill="#ed2132" variants={robotDotVariants(2)} animate="animate" />
              </>
            )}
            <motion.g transform="translate(28, -42)" variants={!prefersReducedMotion ? checkmarkVariants : undefined} animate={!prefersReducedMotion ? "animate" : undefined}>
              <circle r="12" fill="#FFFFFF" stroke="#22C55E" strokeWidth="2" />
              <polyline points="-5,0 -1,4 6,-4" fill="none" stroke="#22C55E" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </motion.g>
            <rect x="-18" y="-22" width="36" height="32" rx="8" fill="#FFFFFF" stroke="#ed2132" strokeWidth="2" />
            <circle cx="-7" cy="-8" r="3.5" fill="#ed2132" />
            <circle cx="7" cy="-8" r="3.5" fill="#ed2132" />
            <line x1="-6" y1="4" x2="6" y2="4" stroke="#ed2132" strokeWidth="2" strokeLinecap="round" />
            <line x1="0" y1="-22" x2="0" y2="-32" stroke="#ed2132" strokeWidth="2" strokeLinecap="round" />
            <circle cx="0" cy="-35" r="4" fill="#ed2132" />
          </g>

          <g fill="none" stroke="#E2E8F0" strokeWidth="1.5">
            <motion.path d="M 842 145 Q 890 110 938 80" strokeDasharray="6 6" variants={!prefersReducedMotion ? fanoutLineVariants(0) : undefined} animate={!prefersReducedMotion ? "animate" : undefined} />
            <motion.path d="M 842 155 Q 890 140 938 130" strokeDasharray="6 6" variants={!prefersReducedMotion ? fanoutLineVariants(1) : undefined} animate={!prefersReducedMotion ? "animate" : undefined} />
            <motion.path d="M 842 175 Q 890 190 938 200" strokeDasharray="6 6" variants={!prefersReducedMotion ? fanoutLineVariants(2) : undefined} animate={!prefersReducedMotion ? "animate" : undefined} />
            <motion.path d="M 842 185 Q 890 220 938 250" strokeDasharray="6 6" variants={!prefersReducedMotion ? fanoutLineVariants(3) : undefined} animate={!prefersReducedMotion ? "animate" : undefined} />
          </g>

          <g>
            <g transform="translate(938, 68)">
              <rect width="70" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
              <text x="35" y="16" fontSize="10" fontWeight="600" fill="#0F172A" textAnchor="middle">Gmail</text>
            </g>
            <g transform="translate(938, 118)">
              <rect width="70" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
              <text x="35" y="16" fontSize="10" fontWeight="600" fill="#0F172A" textAnchor="middle">Slack</text>
            </g>
            <g transform="translate(938, 188)">
              <rect width="70" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
              <text x="35" y="16" fontSize="10" fontWeight="600" fill="#0F172A" textAnchor="middle">Notion</text>
            </g>
            <g transform="translate(938, 238)">
              <rect width="70" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
              <text x="35" y="16" fontSize="10" fontWeight="600" fill="#0F172A" textAnchor="middle">Stripe</text>
            </g>
          </g>

          <g transform="translate(608, 320)">
            <rect x="0" y="0" width="410" height="56" rx="28" fill="#FEE2E2" />
            <circle cx="28" cy="28" r="10" fill="#ed2132" />
            <text x="50" y="38" fontSize="26" fontWeight="600" fill="#ed2132">1,247 tasks automated / wk</text>
          </g>
        </motion.g>

        {/* CARD 3: DEDICATED DEVELOPERS */}
        <motion.g
          animate={{ opacity: getCardOpacity("team") }}
          transition={{ duration: 0.3 }}
          onMouseEnter={() => onServiceHover("team")}
          onMouseLeave={() => onServiceHover(null)}
          onClick={() => handleCardClick("team")}
          style={{ cursor: "pointer" }}
        >
          <motion.rect
            x="1100" y="20" width="440" height="360" rx="14"
            fill="#FFFFFF"
            animate={{ stroke: getCardStroke("team"), strokeWidth: getCardStrokeWidth("team") }}
            transition={{ duration: 0.3 }}
          />
          <text x="1128" y="52" fontSize="11" fontWeight="700" letterSpacing="1.4" fill="#475569">DEDICATED DEVELOPERS</text>

          <g transform="translate(1128, 72)">
            <g><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">JM</text></g>
            <g transform="translate(50,0)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">RK</text></g>
            <g transform="translate(100,0)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">SP</text></g>
            <g transform="translate(0,50)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">AT</text></g>
            <g transform="translate(50,50)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">DV</text></g>
            <g transform="translate(100,50)"><circle cx="20" cy="20" r="20" fill="#FEE2E2" /><text x="20" y="25" fontSize="12" fontWeight="700" fill="#ed2132" textAnchor="middle">NB</text></g>
          </g>

          <g transform="translate(1128, 200)">
            <text x="0" y="0" fontSize="9" fontWeight="600" fill="#475569" letterSpacing="0.5">COMMITS</text>
            <g transform="translate(0, 12)">
              {[
                { x: 0, h: 20 }, { x: 8, h: 28 }, { x: 16, h: 18 }, { x: 24, h: 32 },
                { x: 32, h: 24 }, { x: 40, h: 36 }, { x: 48, h: 22 }, { x: 56, h: 30 },
                { x: 64, h: 18 }, { x: 72, h: 34 }, { x: 80, h: 26 }, { x: 88, h: 20 },
                { x: 96, h: 38 }, { x: 104, h: 28 }, { x: 112, h: 22 }, { x: 120, h: 32 },
                { x: 128, h: 26 }, { x: 136, h: 40, isHighlight: true },
              ].map((bar, i) => (
                <motion.rect
                  key={i}
                  x={bar.x} y={40 - bar.h} width="5" height={bar.h} rx="1"
                  fill={(bar as { isHighlight?: boolean }).isHighlight ? "#ed2132" : "#CBD5E1"}
                  variants={!prefersReducedMotion ? commitBarVariants(i) : undefined}
                  animate={!prefersReducedMotion ? "animate" : undefined}
                  style={{ transformOrigin: `${bar.x + 2.5}px 52px` }}
                  initial={{ scaleY: 1 }}
                />
              ))}
            </g>
          </g>

          <g transform="translate(1320, 72)">
            <motion.g variants={!prefersReducedMotion ? prCardVariants(0) : undefined} animate={!prefersReducedMotion ? "animate" : undefined}>
              <rect x="0" y="0" width="200" height="50" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
              <text x="14" y="18" fontSize="9" fontWeight="600" fill="#475569">PR #248</text>
              <text x="14" y="34" fontSize="10" fontWeight="600" fill="#0F172A">feat: payments flow</text>
              <g transform="translate(138, 16)">
                <rect width="52" height="20" rx="10" fill="#ECFDF5" stroke="#A7F3D0" strokeWidth="1" />
                <polyline points="8,10 12,14 20,6" fill="none" stroke="#059669" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <text x="24" y="14" fontSize="8" fontWeight="700" fill="#059669">Merged</text>
              </g>
            </motion.g>
            <motion.g transform="translate(0, 62)" variants={!prefersReducedMotion ? prCardVariants(1) : undefined} animate={!prefersReducedMotion ? "animate" : undefined}>
              <rect x="0" y="0" width="200" height="50" rx="8" fill="#F8FAFC" stroke="#E2E8F0" strokeWidth="1" />
              <text x="14" y="18" fontSize="9" fontWeight="600" fill="#475569">PR #251</text>
              <text x="14" y="34" fontSize="10" fontWeight="600" fill="#0F172A">fix: webhook retry</text>
              <g transform="translate(138, 16)">
                <rect width="52" height="20" rx="10" fill="#ECFDF5" stroke="#A7F3D0" strokeWidth="1" />
                <polyline points="8,10 12,14 20,6" fill="none" stroke="#059669" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                <text x="24" y="14" fontSize="8" fontWeight="700" fill="#059669">Merged</text>
              </g>
            </motion.g>
          </g>

          <g transform="translate(1128, 320)">
            <rect x="0" y="0" width="390" height="56" rx="28" fill="#FEE2E2" />
            <circle cx="28" cy="28" r="10" fill="#ed2132" />
            <text x="50" y="38" fontSize="26" fontWeight="600" fill="#ed2132">Senior devs, integrated</text>
          </g>
        </motion.g>

        {/* CENTER LAUNCH NODE */}
        <g transform="translate(800, 500)">
          <circle r="56" fill="#FFFFFF" stroke="#FECACA" strokeWidth="2" />
          <circle r="48" fill="#FFFFFF" stroke="#FEE2E2" strokeWidth="1.5" />
          <g stroke="#ed2132" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" transform="translate(-16, -16)">
            <path d="M 3 28 L 12 19 L 22 9 Q 29 2 31 0 Q 26 -2 22 0 Q 19 3 12 10 L 3 19 Z" fill="#ed2132" stroke="none" />
            <circle cx="19" cy="12" r="2.5" fill="#FFFFFF" stroke="#FFFFFF" />
            <path d="M 6 22 L 2 23 L 3 28 Z" fill="#ed2132" stroke="none" />
            <path d="M 10 26 L 9 31 L 3 28 Z" fill="#ed2132" stroke="none" />
            <path d="M 3 28 L -2 33 L 2 33 L 0 36 L 5 33 Z" fill="#FECACA" stroke="none" />
          </g>
          <g transform="translate(-60, -82)">
            <rect width="120" height="24" rx="12" fill="#FFFFFF" stroke="#E2E8F0" strokeWidth="1" />
            <motion.g variants={!prefersReducedMotion ? statusDotVariants : undefined} animate={!prefersReducedMotion ? "animate" : undefined}>
              <circle cx="14" cy="12" r="6" fill="#ed2132" opacity="0.25" />
              <circle cx="14" cy="12" r="3.5" fill="#ed2132" />
            </motion.g>
            <text x="28" y="16" fontSize="10" fontWeight="600" fill="#0F172A">Live in production</text>
          </g>
        </g>
      </svg>
    </div>
  )
}
